import React, { useEffect, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { CalendarIcon, ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon, XMarkIcon } from "@heroicons/react/24/outline"
import moment from "moment"
import Button from "aldoo-ra/Button"
import DatePicker from "aldoo-ra/DatePicker"
import { formatPeriod } from "./format"
import { shiftRange, startsInFuture, formatPeriod as unitLabel } from "aldoo-ra/DateFilterDropdown/periods"
import { WeekPanel, MonthPanel, QuarterPanel, YearPanel } from "aldoo-ra/DateFilterDropdown/PeriodPanels"
import { ADMIN_PERIOD } from "aldoo-ra/DateFilterDropdown/variants"
import useDashboardTexts from "./useDashboardTexts"

export const PRESETS = [
  ["today", "Today"],
  ["yesterday", "Yesterday"],
  ["last_7_days", "Last 7 days"],
  ["last_28_days", "Last 28 days"],
  ["last_30_days", "Last 30 days"],
  ["last_90_days", "Last 90 days"],
  ["this_month", "This month"],
  ["last_month", "Last month"],
  ["this_year", "This year"],
  ["last_year", "Last year"],
  ["all_time", "All time"],
  ["custom", "Custom"],
]

const COMPARE = [
  ["previous_period", "Previous period"],
  ["previous_week", "A week before"],
  ["previous_year", "Same period last year"],
  ["custom", "Custom"],
  ["none", "No comparison"],
]

// the tabs over the date ranges, as the date filter of the sales report has them:
// the presets, or a whole week, month, quarter or year picked from a calendar
const RANGE_TABS = [
  ["range", "Range"],
  ["week", "Week"],
  ["month", "Month"],
  ["quarter", "Quarter"],
  ["year", "Year"],
]
const PANELS = { week: WeekPanel, month: MonthPanel, quarter: QuarterPanel, year: YearPanel }

// what an arrow steps a preset by: a month or a year by its unit, the rest by its days
const STEP_UNIT = { this_month: "month", last_month: "month", this_year: "year", last_year: "year" }
const stepUnitOf = (range) => range?.unit || STEP_UNIT[range?.preset] || null
// local dates ↔ the { startDate, endDate } the date filter's helpers take
const toRange = (p) => (p?.from && p?.to ? { startDate: moment(p.from).toDate(), endDate: moment(p.to).toDate() } : null)
const toDates = (r) => ({ from: moment(r.startDate).format("YYYY-MM-DD"), to: moment(r.endDate).format("YYYY-MM-DD") })

/**
 * The range one step before (`dir` -1) or after (+1) the one shown (`period`,
 * as the server resolved it): a week, month, quarter or year by its unit, any
 * other range by its own number of days. A custom comparison moves with it.
 */
function stepOf(range, compare, period, dir) {
  if (range?.preset === "all_time") return null
  const base = toRange(period)
  if (!base) return null
  const unit = stepUnitOf(range)
  const next = shiftRange(base, unit || "custom", dir)
  if (!next) return null
  const nextRange = { preset: "custom", ...toDates(next), ...(unit ? { unit } : {}) }
  if (compare?.mode !== "custom" || !compare.from || !compare.to) return { range: nextRange, compare, next }
  const days = moment(next.startDate).diff(moment(base.startDate), "days")
  const moved = unit
    ? shiftRange(toRange(compare), unit === "week" ? "custom" : unit, dir)
    : { startDate: moment(compare.from).add(days, "days").toDate(), endDate: moment(compare.to).add(days, "days").toDate() }
  return { range: nextRange, compare: { mode: "custom", ...toDates(moved) }, next }
}

const DAY = 24 * 60 * 60 * 1000
const iso = (ms) => new Date(ms).toISOString().slice(0, 10)
const parse = (date) => Date.UTC(Number(date.slice(0, 4)), Number(date.slice(5, 7)) - 1, Number(date.slice(8, 10)))
const todayIso = () => {
  const now = new Date()
  return iso(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()))
}

/** A range's local dates, as the server resolves its presets (for the comparison's length). */
function datesOf(range) {
  if (range?.preset === "custom" || !range?.preset) return range?.from && range?.to ? [range.from, range.to] : null
  const t = parse(todayIso())
  const d = new Date(t)
  const day = (n) => iso(t - n * DAY)
  switch (range.preset) {
    case "today": return [day(0), day(0)]
    case "yesterday": return [day(1), day(1)]
    case "this_month": return [iso(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1)), day(0)]
    case "last_month": return [iso(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() - 1, 1)), iso(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 0))]
    case "this_year": return [iso(Date.UTC(d.getUTCFullYear(), 0, 1)), day(0)]
    case "last_year": return [iso(Date.UTC(d.getUTCFullYear() - 1, 0, 1)), iso(Date.UTC(d.getUTCFullYear() - 1, 11, 31))]
    default: return null
  }
}

/**
 * The unit a range is: one day, a month, a year — then a custom comparison
 * is one of the same — or a stretch of days, compared with two dates.
 */
function unitOf(range) {
  if (["today", "yesterday"].includes(range?.preset)) return "day"
  if (["this_month", "last_month"].includes(range?.preset) || range?.unit === "month") return "month"
  if (["this_year", "last_year"].includes(range?.preset) || range?.unit === "year") return "year"
  if (range?.preset === "custom" && range.from && range.from === range.to) return "day"
  return "range"
}

/**
 * The comparison for a month or a year picked on its own: from its first day,
 * as many days as the range has (so "This month", half over, meets the same
 * half of the month picked), never past its end.
 */
function unitComparison(unit, value, range) {
  const dates = datesOf(range)
  const days = dates ? Math.round((parse(dates[1]) - parse(dates[0])) / DAY) + 1 : null
  if (unit === "month") {
    const [y, m] = value.split("-").map(Number)
    const from = Date.UTC(y, m - 1, 1)
    const last = Date.UTC(y, m, 0)
    return { from: iso(from), to: iso(days ? Math.min(last, from + (days - 1) * DAY) : last) }
  }
  const y = Number(value)
  const from = Date.UTC(y, 0, 1)
  const last = Date.UTC(y, 11, 31)
  return { from: iso(from), to: iso(days ? Math.min(last, from + (days - 1) * DAY) : last) }
}

/** The unit right before a range's, to start a custom comparison from. */
function previousUnit(unit, range) {
  const dates = datesOf(range)
  if (!dates) return {}
  const [y, m, d] = dates[0].split("-").map(Number)
  if (unit === "day") {
    const day = iso(Date.UTC(y, m - 1, d - 1))
    return { from: day, to: day }
  }
  if (unit === "month") return unitComparison("month", iso(Date.UTC(y, m - 2, 1)).slice(0, 7), range)
  return unitComparison("year", String(y - 1), range)
}

const ymd = (date) => moment(date).format("YYYY-MM-DD")

/**
 * A period picked with the aldoo-ra DatePicker, as the unit asks: one day,
 * one month, one year, or two days (`unit` "range"). It reads and hands back
 * { from, to } local dates; a month or a year is handed back as its value
 * ("YYYY-MM" / "YYYY") for the caller to size.
 */
function PeriodField({ unit, value, onPick, testId }) {
  const t = useDashboardTexts()
  const byRange = unit === "range"
  const from = value?.from ? moment(value.from) : null
  const to = value?.to ? moment(value.to) : null
  const text = !from
    ? t`Select`
    : unit === "month"
      ? from.format("MMM YYYY")
      : unit === "year"
        ? from.format("YYYY")
        : byRange && to && !to.isSame(from, "day")
          ? `${from.format("DD.MM.YYYY")} – ${to.format("DD.MM.YYYY")}`
          : from.format("DD.MM.YYYY")

  return (
    <DatePicker
      variant="admin"
      range={byRange}
      granularity={byRange ? "day" : unit}
      dateFormat="DD.MM.YYYY"
      value={byRange ? (value?.from && value?.to ? { startDate: value.from, endDate: value.to } : null) : value?.from || null}
      onChange={(picked) => {
        if (byRange) return onPick({ from: ymd(picked.startDate), to: ymd(picked.endDate) })
        if (unit === "month") return onPick(moment(picked).format("YYYY-MM"))
        if (unit === "year") return onPick(moment(picked).format("YYYY"))
        const day = ymd(picked)
        onPick({ from: day, to: day })
      }}
      triggerClassName="flex w-full mt-1 cursor-pointer"
      trigger={
        <span className="flex items-center gap-2 w-full h-9 px-2.5 rounded-lg border border-admin_bordered bg-admin_paper text-[13px] text-admin_text hover:border-admin_primary tabular-nums" data-testid={testId}>
          <CalendarIcon className="w-4 h-4 text-admin_text_secondary shrink-0" />
          <span className="truncate">{text}</span>
        </span>
      }
    />
  )
}

/**
 * The period a dashboard shows and what it is compared with, the way
 * Google Analytics asks it: a preset or two dates, and a comparison.
 */
export default function PeriodPicker({ range, compare, period, comparison, onChange, timeZone, className = "", showArrows = true }) {
  const t = useDashboardTexts()
  const [open, setOpen] = useState(false)
  const [draft, setDraft] = useState({ range, compare })
  const ref = useRef(null)
  const panelRef = useRef(null)
  // a phone shows one list at a time, chosen with the segmented tabs on top
  const [pane, setPane] = useState("range")
  // the tab over the date ranges: the presets, or the calendar of a unit
  const [rangeTab, setRangeTab] = useState("range")
  // on a phone the panel covers the page: it is set at the page's top, above the site's own bar
  const [phone, setPhone] = useState(false)
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)")
    const update = () => setPhone(mq.matches)
    update()
    mq.addEventListener("change", update)
    return () => mq.removeEventListener("change", update)
  }, [])

  useEffect(() => {
    if (!open) return
    setDraft({ range: { ...range }, compare: { ...compare } })
    setPane("range")
    setRangeTab(PANELS[range?.unit] ? range.unit : "range")
  }, [open])
  useEffect(() => {
    if (!open) return
    const close = (e) => !ref.current?.contains(e.target) && !panelRef.current?.contains(e.target) && setOpen(false)
    document.addEventListener("mousedown", close)
    return () => document.removeEventListener("mousedown", close)
  }, [open])

  // what a custom comparison is picked as: a day, a month, a year, or two dates
  const unit = unitOf(draft.range)
  const presetLabel = t(PRESETS.find(([id]) => id === (range?.preset || "custom"))?.[1])
  const radio = (on) => `w-full text-left px-3 py-1.5 rounded-lg text-sm ${on ? "bg-admin_primary/10 text-admin_primary font-semibold" : "text-admin_text hover:bg-admin_neutral_1"}`

  // a new range in the draft; a custom comparison follows the range's unit: a new unit starts it over
  const pickRange = (nextOf) =>
    setDraft((d) => {
      const next = nextOf(d)
      if (d.compare?.mode !== "custom" || unitOf(next) === unitOf(d.range)) return { ...d, range: next }
      const u = unitOf(next)
      return { range: next, compare: { mode: "custom", ...(u === "range" ? { from: comparison?.from, to: comparison?.to } : previousUnit(u, next)) } }
    })
  const Panel = PANELS[rangeTab]

  // the arrows beside the button: the range before or after, at once
  const previousStep = showArrows ? stepOf(range, compare, period, -1) : null
  const nextStep = showArrows ? stepOf(range, compare, period, 1) : null
  const canStepNext = !!nextStep && !startsInFuture(nextStep.next)
  const stepTo = (s) => s && onChange({ range: s.range, compare: s.compare })
  const arrow = "shrink-0 p-1 rounded-md text-admin_primary hover:bg-admin_primary/10 disabled:opacity-30 disabled:hover:bg-transparent disabled:cursor-default"

  const panelOf = () => (
        // on a phone the whole screen, its buttons kept at the bottom; from 768px a panel under the button
        <div ref={panelRef} className="fixed inset-0 z-[500] flex flex-col bg-admin_paper md:absolute md:inset-auto md:right-0 md:top-full md:mt-2 md:z-30 md:w-[min(94vw,640px)] md:rounded-xl md:border md:border-admin_bordered md:shadow-xl" data-testid="dashboard-period-panel">
          <div className="md:hidden flex items-center justify-between h-14 px-4 border-b border-admin_bordered shrink-0">
            <span className="text-base font-bold text-admin_text">{t`Period`}</span>
            <button type="button" onClick={() => setOpen(false)} className="p-2 -mr-2 text-admin_text_secondary" aria-label={t`Close`}>
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto p-4 md:overflow-visible md:flex-none">
          {/* the two lists as iOS-style segments: Date range | Compare to */}
          <div className="md:hidden grid grid-cols-2 p-1 mb-4 rounded-xl bg-admin_neutral_1" role="tablist" data-testid="period-panes">
            {[
              ["range", t`Date range`],
              ["compare", t`Compare to`],
            ].map(([id, label]) => (
              <button
                key={id}
                type="button"
                role="tab"
                aria-selected={pane === id}
                onClick={() => setPane(id)}
                className={`h-9 rounded-lg text-sm font-semibold transition-colors ${pane === id ? "bg-admin_paper text-admin_text shadow-sm" : "text-admin_text_secondary"}`}
                data-testid={`period-pane-${id}`}
              >
                {label}
              </button>
            ))}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-4">
            <div className={`flex-col gap-1 ${pane === "range" ? "flex" : "hidden md:flex"}`}>
              <span className="max-md:hidden text-xs font-semibold uppercase tracking-wide text-admin_text_secondary mb-1">{t`Date range`}</span>
              {/* Range | Week | Month | Quarter | Year */}
              <div className={ADMIN_PERIOD.tabs} role="tablist" data-testid="period-range-tabs">
                {RANGE_TABS.map(([id, label]) => (
                  <button
                    key={id}
                    type="button"
                    role="tab"
                    aria-selected={rangeTab === id}
                    className={`${ADMIN_PERIOD.tab.base} ${rangeTab === id ? ADMIN_PERIOD.tab.active : ADMIN_PERIOD.tab.idle}`}
                    onClick={() => setRangeTab(id)}
                    data-testid={`period-range-tab-${id}`}
                  >
                    {t(label)}
                  </button>
                ))}
              </div>
              {Panel && (
                <Panel
                  v={ADMIN_PERIOD}
                  value={draft.range?.unit === rangeTab ? toRange(draft.range) : null}
                  minYear={moment().year() - 25}
                  onPick={(picked) => pickRange(() => ({ preset: "custom", ...toDates(picked), unit: rangeTab }))}
                />
              )}
              {rangeTab === "range" && PRESETS.map(([id, label]) => (
                <button key={id} type="button" className={radio((draft.range?.preset || "custom") === id && !draft.range?.unit)} onClick={() => pickRange((d) => (id === "custom" ? { preset: "custom", from: d.range?.from || period?.from, to: d.range?.to || period?.to } : { preset: id }))}>
                  {t(label)}
                </button>
              ))}
              {rangeTab === "range" && draft.range?.preset === "custom" && !draft.range?.unit && (
                <PeriodField unit="range" value={draft.range} onPick={(picked) => setDraft((d) => ({ ...d, range: { preset: "custom", ...picked } }))} testId="range-custom" />
              )}
            </div>
            <div className={`flex-col gap-1 ${pane === "compare" ? "flex" : "hidden md:flex"}`}>
              <span className="max-md:hidden text-xs font-semibold uppercase tracking-wide text-admin_text_secondary mb-1">{t`Compare to`}</span>
              {/* all of time has nothing before it to compare with */}
              {draft.range?.preset === "all_time" && (
                <span className="px-3 py-1.5 text-sm text-admin_text_secondary" data-testid="compare-none-all-time">
                  {t`Nothing to compare with: all time has no period before it`}
                </span>
              )}
              {draft.range?.preset !== "all_time" && COMPARE.map(([id, label]) => (
                <button key={id} type="button" className={radio((draft.compare?.mode || "previous_period") === id)} onClick={() => setDraft((d) => {
                  if (id !== "custom") return { ...d, compare: { mode: id } }
                  const u = unitOf(d.range)
                  if (u !== "range") return { ...d, compare: { mode: "custom", ...previousUnit(u, d.range) } }
                  return { ...d, compare: { mode: "custom", from: d.compare?.from || comparison?.from, to: d.compare?.to || comparison?.to } }
                })}>
                  {t(label)}
                </button>
              ))}
              {draft.compare?.mode === "custom" && draft.range?.preset !== "all_time" && (
                <PeriodField
                  unit={unit}
                  value={draft.compare}
                  onPick={(picked) => setDraft((d) => ({
                    ...d,
                    compare: { mode: "custom", ...(typeof picked === "string" ? unitComparison(unit, picked, d.range) : picked) },
                  }))}
                  testId={`compare-${unit}`}
                />
              )}
            </div>
          </div>
            {timeZone && <p className="md:hidden mt-6 mb-0 text-[11px] text-admin_text_secondary">{t("Days are counted in %s", timeZone.replace(/_/g, " "))}</p>}
          </div>
          <div className="flex items-center justify-end gap-2 shrink-0 p-4 pt-0 max-md:pt-3 max-md:border-t max-md:border-admin_bordered">
            {/* where the days begin and end: the scope's reporting time zone */}
            {timeZone && (
              <span className="mr-auto text-[11px] text-admin_text_secondary max-md:hidden" data-testid="period-time-zone">
                {t("Days are counted in %s", timeZone.replace(/_/g, " "))}
              </span>
            )}
            <Button variant="admin_secondary" onClick={() => setOpen(false)} className="max-md:flex-1">{t`Cancel`}</Button>
            <Button
              variant="admin_primary"
              className="max-md:flex-1"
              disabled={(draft.range?.preset === "custom" && !(draft.range.from && draft.range.to)) || (draft.range?.preset !== "all_time" && draft.compare?.mode === "custom" && !(draft.compare.from && draft.compare.to))}
              onClick={() => {
                // all time is kept with no comparison: what it would compare with does not exist
                onChange(draft.range?.preset === "all_time" ? { ...draft, compare: { mode: "none" } } : draft)
                setOpen(false)
              }}
              data-testid="dashboard-period-apply"
            >
              {t`Apply`}
            </Button>
          </div>
        </div>
  )

  return (
    <div ref={ref} className={`relative flex items-center gap-1 ${className}`} data-testid="dashboard-period">
      {showArrows && (
        <button type="button" className={arrow} disabled={!previousStep} onClick={() => stepTo(previousStep)} aria-label={t`Previous period`} data-testid="dashboard-period-prev">
          <ChevronLeftIcon className="w-5 h-5" />
        </button>
      )}
      <button type="button" onClick={() => setOpen(!open)} className="flex-1 min-w-0 md:flex-none md:w-auto flex items-center gap-2 h-10 px-3 rounded-lg border border-admin_bordered bg-admin_paper text-left hover:border-admin_primary">
        <CalendarIcon className="w-5 h-5 text-admin_text_secondary shrink-0" />
        <span className="flex-1 min-w-0 flex flex-col leading-tight">
          <span className="text-sm text-admin_text truncate">
            {presetLabel && range?.preset !== "custom" ? `${presetLabel} · ` : ""}
            {/* a whole month, quarter or year says itself: "Sep 2026", "Q3 2026", "2026"; a week its days */}
            {range?.unit && range.unit !== "week" && toRange(range) ? unitLabel(toRange(range), range.unit) : formatPeriod(period)}
          </span>
          {comparison && <span className="text-[11px] text-admin_text_secondary truncate">{t("vs %s", formatPeriod(comparison))}</span>}
        </span>
        <ChevronDownIcon className="w-4 h-4 text-admin_text_secondary" />
      </button>
      {showArrows && (
        <button type="button" className={arrow} disabled={!canStepNext} onClick={() => stepTo(nextStep)} aria-label={t`Next period`} data-testid="dashboard-period-next">
          <ChevronRightIcon className="w-5 h-5" />
        </button>
      )}

      {open && (phone ? createPortal(panelOf(), document.body) : panelOf())}
    </div>
  )
}
